import debounce from 'lodash/debounce';
import { useEffect, useMemo, useRef } from 'react';

/**
 * Custom React hook that debounces a callback function.
 * It ensures that the provided callback is only executed after a specified delay
 * since the last time the debounced function was invoked.
 * The hook also handles cleanup by cancelling any pending debounced calls when
 * the component unmounts or when the debounced function is recreated due to
 * changes in `delay` or `options`.
 */
const useDebounce = <T extends (...args: any[]) => any>(
    callback: T,
    delay = 300,
    options?: Parameters<typeof debounce>[2]
) => {
    const ref = useRef<T>();

    useEffect(() => {
        ref.current = callback;
    }, [callback]);

    const debouncedCallback = useMemo(() => {
        const func = (...args: Parameters<T>) => {
            ref.current?.(...args);
        };
        return debounce(func, delay, options);
    }, [delay, options]);

    useEffect(() => {
        return () => {
            debouncedCallback.cancel();
        };
    }, [debouncedCallback]);

    return debouncedCallback;
};

export default useDebounce;
